// src/pages/HomePublic.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  listActiveProducts,
  listPublicCategories,
} from "../services/publicProductService";
import { useCart } from "../context/CartContext";
import { listActiveHomeSections } from "../services/homeSectionService";
import HomeRenderer from "../components/HomeRenderer";
import PromoProducts from "../components/PromoProducts";
import {
  getActivePromo,
  getActivePromoForPlacement,
} from "../services/promoService.firebase";
import Seo from "../components/Seo";
import BannersSliderFirestore from "../components/BannersSliderFirestore";

/* ---------- Helpers ---------- */
const isInStockProduct = (p) => {
  const variants = Array.isArray(p?.variants) ? p.variants : [];
  if (variants.length > 0) return variants.some((v) => Number(v?.stock || 0) > 0);
  return Number(p?.stock || 0) > 0;
};

const dedupeByIdOrSlug = (arr = []) => {
  const out = [];
  const seen = new Set();
  for (const p of arr) {
    const key = p?.id || p?.slug;
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
};

const fmtXof = (n) =>
  new Intl.NumberFormat("fr-FR").format(Math.round(Number(n || 0))) + " FCFA";

const getProductHref = (p) => {
  const slug = p?.slug || p?.id;
  return slug ? `/p/${slug}` : "#";
};

const shuffle = (arr = []) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* ====== Skeletons ====== */
function SkeletonGrid({ n = 8 }) {
  return (
    <div className="row row-cols-2 row-cols-sm-2 row-cols-md-4 row-cols-lg-4 g-2 g-md-3">
      {Array.from({ length: n }).map((_, i) => (
        <div className="col" key={`sk-${i}`}>
          <div className="card h-100 shadow-sm">
            <div className="placeholder-glow">
              <div
                className="card-img-top"
                style={{ height: "clamp(120px, 28vw, 220px)", background: "#eee" }}
              />
              <div className="card-body">
                <span className="placeholder col-6" />
                <div className="mt-2">
                  <span className="placeholder col-8" />
                </div>
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <span className="placeholder col-3" />
                  <span className="placeholder col-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonRows({ n = 8 }) {
  return (
    <div className="vstack gap-2">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="card shadow-sm border-0">
          <div className="card-body d-flex gap-3 align-items-center placeholder-glow">
            <div style={{ width: 84, height: 84, background: "#eee", borderRadius: 12 }} />
            <div className="flex-grow-1">
              <div className="placeholder col-6" />
              <div className="placeholder col-8 mt-2" />
              <div className="placeholder col-3 mt-3" />
            </div>
            <div style={{ width: 140 }}>
              <div className="placeholder col-12" />
              <div className="placeholder col-10 mt-2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================
   Mobile Category Bar (simple)
========================= */
function MobileCategoryBar({ cats = [], selectedId, onSelect }) {
  const getId = (c) => c?.id || c?.slug || "";

  const list = useMemo(() => {
    const vis = (Array.isArray(cats) ? cats : []).filter((x) => x?.visible === true);
    vis.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr"));
    return vis.slice(0, 20);
  }, [cats]);

  if (!list.length) return null;

  return (
    <div className="d-lg-none mb-2">
      <div className="ggf-mcats">
        <button
          type="button"
          className={`ggf-mcats__pill ${!selectedId ? "is-active" : ""}`}
          onClick={() => onSelect(null)}
        >
          <i className="bi bi-grid-3x3-gap me-2" />
          Tout
        </button>

        {list.map((c) => {
          const cid = getId(c);
          return (
            <button
              key={cid}
              type="button"
              className={`ggf-mcats__pill ${
                String(selectedId || "") === String(cid) ? "is-active" : ""
              }`}
              onClick={() => onSelect(c)}
              title={c.name}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      <style>{`
        .ggf-mcats{
          display:flex;
          gap:10px;
          overflow-x:auto;
          padding: 8px 2px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }
        .ggf-mcats::-webkit-scrollbar{ display:none; }
        .ggf-mcats__pill{
          flex: 0 0 auto;
          border: 1px solid rgba(0,0,0,.12);
          background: #fff;
          color: #111;
          border-radius: 999px;
          padding: 9px 12px;
          font-weight: 900;
          font-size: .88rem;
          white-space: nowrap;
          box-shadow: 0 8px 18px rgba(0,0,0,.06);
        }
        .ggf-mcats__pill.is-active{
          background:#111;
          color:#fff;
          border-color:#111;
        }
        .ggf-mcats__pill:active{ transform: translateY(1px); }
      `}</style>
    </div>
  );
}

/* =========================
   ✅ Desktop Sidebar Catégories (style comme ton image)
   - Carte avec gros titre "Catégories"
   - Bouton "Tout afficher" noir avec chevron bas
   - Items avec chevron droit
   - Sous-menu flyout à droite (Robes / Ensembles / etc.)
========================= */
function CategorySidebar({ cats = [], selectedId, onSelect }) {
  const [hovered, setHovered] = useState(null);

  const { parents, childrenByParent } = useMemo(() => {
    const vis = (Array.isArray(cats) ? cats : []).filter((x) => x?.visible === true);

    const byParent = new Map();
    for (const c of vis) {
      const pid = c.parentId || null;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid).push(c);
    }

    const parentsList = (byParent.get(null) || []).slice();
    parentsList.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr"));

    for (const [pid, arr] of byParent.entries()) {
      const sorted = arr.slice().sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "fr")
      );
      byParent.set(pid, sorted);
    }

    return { parents: parentsList, childrenByParent: byParent };
  }, [cats]);

  if (!parents.length) return null;

  const getId = (c) => c?.id || c?.slug || "";
  const isSelected = (c) => getId(c) && getId(c) === String(selectedId || "");
  const hasChildren = (p) => (childrenByParent.get(p.id) || []).length > 0;

  return (
    <aside className="ggf-cats2 d-none d-lg-block" onMouseLeave={() => setHovered(null)}>
      <div className="ggf-cats2__card">
        <div className="ggf-cats2__title">Catégories</div>

        {/* "Tout afficher" comme ton image */}
        <button
          type="button"
          className={`ggf-cats2__all ${!selectedId ? "is-active" : ""}`}
          onClick={() => onSelect(null)}
        >
          <span>Tout afficher</span>
          <i className="bi bi-chevron-down" />
        </button>

        <div className="ggf-cats2__listwrap position-relative">
          <ul className="ggf-cats2__list">
            {parents.map((p) => {
              const pid = getId(p);
              const open = hovered && getId(hovered) === pid;
              return (
                <li
                  key={pid}
                  className={`ggf-cats2__item ${isSelected(p) ? "is-selected" : ""} ${
                    open ? "is-hover" : ""
                  }`}
                  onMouseEnter={() => setHovered(hasChildren(p) ? p : null)}
                >
                  <button
                    type="button"
                    className="ggf-cats2__link"
                    onClick={() => onSelect(p)}
                    title={p.name}
                  >
                    <span className="ggf-cats2__txt">{p.name}</span>
                    {hasChildren(p) ? <i className="bi bi-chevron-right" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Flyout sous-catégories */}
          {hovered && hasChildren(hovered) && (
            <div className="ggf-cats2__flyout">
              <div className="ggf-cats2__flylist">
                {(childrenByParent.get(hovered.id) || []).map((c) => {
                  const cid = getId(c);
                  return (
                    <button
                      key={cid}
                      type="button"
                      className={`ggf-cats2__flyitem ${isSelected(c) ? "is-selected" : ""}`}
                      onClick={() => onSelect(c)}
                      title={c.name}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ggf-cats2__card{
          background:#fff;
          border-radius: 18px;
          border: 1px solid rgba(0,0,0,.07);
          box-shadow: 0 18px 46px rgba(0,0,0,.08);
          padding: 18px 16px 16px;
          min-height: 520px;
        }
        .ggf-cats2__title{
          font-weight: 900;
          font-size: 1.9rem;
          letter-spacing: -0.02em;
          margin-bottom: 16px;
          color:#111;
        }

        .ggf-cats2__all{
          width:100%;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          border: 1px solid rgba(0,0,0,.10);
          background: #121826;
          color:#fff;
          border-radius: 12px;
          padding: 14px 16px;
          font-weight: 900;
          font-size: 1.05rem;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,.06);
        }
        .ggf-cats2__all i{ opacity:.9; font-size: 1.1rem; }
        .ggf-cats2__all:active{ transform: translateY(1px); }

        .ggf-cats2__listwrap{ margin-top: 14px; position:relative; }
        .ggf-cats2__list{
          list-style:none;
          margin:0;
          padding:0;
          display:flex;
          flex-direction:column;
          gap: 6px;
        }

        .ggf-cats2__item{
          border-radius: 12px;
        }
        .ggf-cats2__link{
          width:100%;
          border:0;
          background: transparent;
          padding: 14px 12px;
          border-radius: 12px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          font-size: 1.15rem;
          font-weight: 500;
          color:#111;
        }
        .ggf-cats2__txt{
          display:-webkit-box;
          -webkit-line-clamp:1;
          -webkit-box-orient:vertical;
          overflow:hidden;
        }
        .ggf-cats2__item.is-hover .ggf-cats2__link{
          background: #f3f4f6;
        }
        .ggf-cats2__item.is-selected .ggf-cats2__link{
          background:#f3f4f6;
          font-weight: 800;
        }

        .ggf-cats2__flyout{
          position:absolute;
          top: 82px;              /* aligne visuellement comme l'image */
          left: calc(100% - 8px);  /* “collé” au bord */
          width: 320px;
          background:#fff;
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,.07);
          box-shadow: 0 18px 46px rgba(0,0,0,.12);
          overflow:hidden;
          z-index: 50;
        }
        .ggf-cats2__flylist{
          display:flex;
          flex-direction:column;
        }
        .ggf-cats2__flyitem{
          text-align:left;
          border:0;
          background:#fff;
          padding: 16px 16px;
          font-size: 1.25rem;
          font-weight: 500;
          color:#111;
          border-bottom: 1px solid rgba(0,0,0,.06);
        }
        .ggf-cats2__flyitem:last-child{ border-bottom:0; }
        .ggf-cats2__flyitem:hover{
          background:#f6f7f9;
        }
        .ggf-cats2__flyitem.is-selected{
          background:#121826;
          color:#fff;
          font-weight: 800;
        }
      `}</style>
    </aside>
  );
}

/* =========================
   ✅ PRODUITS MIS EN AVANT (style comme ton image)
========================= */
    
function FeaturedShowcase({ items = [], subtitle = "Découvrez nos produits phares" }) {
  if (!items.length) return null;

  return (
    <section className="ggf-featured mb-4 mb-md-5">
      <div className="text-center mb-3 mb-md-4">
        <div className="ggf-featured__sub">{subtitle}</div>
      </div>

      <div className="row row-cols-2 row-cols-md-4 g-3 g-md-4">
        {items.slice(0, 4).map((p) => {
          const img = p?.images?.[0] || "";
          const price = Number(p?.price ?? 0);
          const href = getProductHref(p);

          return (
            <div className="col" key={p.id || p.slug}>
              <div className="ggf-featured__card h-100">
                <Link to={href} className="ggf-featured__imgwrap" aria-label={p?.title || p?.name || "Produit"}>
                  {img ? (
                    <img
                      src={img}
                      alt={p.title || p.name || "Produit"}
                      loading="lazy"
                      onError={(e) => (e.currentTarget.src = "/img/placeholder.png")}
                    />
                  ) : (
                    <div className="w-100 h-100 bg-light" />
                  )}
                </Link>

                <div className="ggf-featured__body">
                  <div className="ggf-featured__name" title={p.title || p.name}>
                    {p.title || p.name || "Produit"}
                  </div>

                  <div className="ggf-featured__desc">
                    {p?.shortDescription || p?.subtitle || "Sélection tendance"}
                  </div>

                  <div className="ggf-featured__price">{fmtXof(price)}</div>

                  <Link to={href} className="btn btn-dark btn-sm w-100 ggf-featured__btn">
                    Voir le produit
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .ggf-featured {
          padding: clamp(14px, 2.2vw, 22px) 0;
        }

        .ggf-featured__sub {
          margin-top: 10px;
          color: rgba(17,17,17,.65);
          font-weight: 600;
          font-size: clamp(.95rem, 1.5vw, 1.15rem);
        }

        .ggf-featured__card {
          background: #fff;
          border-radius: 16px;
          border: 1px solid rgba(0,0,0,.08);
          box-shadow: 0 14px 32px rgba(0,0,0,.08);
          overflow: hidden;
          transition: transform .16s ease, box-shadow .16s ease;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .ggf-featured__card:hover {
          transform: translateY(-2px);
          box-shadow: 0 18px 40px rgba(0,0,0,.12);
        }

        .ggf-featured__imgwrap {
          display: block;
          background: #f6f7f9;
          padding: 18px;
        }

        .ggf-featured__imgwrap img {
          width: 100%;
          height: 160px;
          object-fit: contain;
          display: block;
          filter: drop-shadow(0 10px 18px rgba(0,0,0,.10));
        }

        @media (min-width: 768px) {
          .ggf-featured__imgwrap img {
            height: 190px;
          }
        }

        .ggf-featured__body {
          padding: 16px 16px 18px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          flex: 1;
          text-align: center;
        }

        .ggf-featured__name {
          font-weight: 900;
          font-size: 1.02rem;
          color: #111;
          line-height: 1.15;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 2.3em;
        }

        .ggf-featured__desc {
          color: rgba(17,17,17,.62);
          font-weight: 600;
          font-size: .92rem;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 1.25em;
        }

        .ggf-featured__price {
          font-weight: 900;
          font-size: 1.1rem;
          color: #111;
          margin-top: 2px;
        }

        .ggf-featured__btn {
          margin-top: auto;
          border-radius: 10px;
          padding: 10px 14px;
          font-weight: 800;
        }

        /* Responsive design for mobile */
        @media (max-width: 576px) {
          .ggf-featured__card {
            box-shadow: none;
            border: none;
            margin-bottom: 20px;
          }

          .ggf-featured__name {
            font-size: 1rem;
            min-height: 2em;
          }

          .ggf-featured__desc {
            font-size: 0.9rem;
            min-height: 2.2em;
          }

          .ggf-featured__price {
            font-size: 1.2rem;
          }

          .ggf-featured__btn {
            padding: 8px 12px;
            font-size: 0.9rem;
          }
        }
      `}</style>
    </section>
  );
        }
/* =========================
   Vue CARD : Grille
========================= */
function ProductsCardGrid({ items = [], onAdd, title }) {
  return (
    <section className="mb-3">
      {title ? <h2 className="h6 mb-2">{title}</h2> : null}

      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-4 row-cols-lg-4 g-2 g-md-3">
        {items.map((p) => {
          const img = p?.images?.[0] || "";
          const inStock = isInStockProduct(p);
          const price = Number(p?.price ?? 0);

          return (
            <div className="col" key={p.id || p.slug}>
              <div className="card h-100 shadow-sm border-0 d-flex flex-column">
                <Link
                  to={getProductHref(p)}
                  className="text-decoration-none"
                  style={{ color: "inherit" }}
                >
                  <div className="ratio ratio-1x1 bg-light">
                    {img ? (
                      <img
                        src={img}
                        alt={p.title || p.name || "Produit"}
                        loading="lazy"
                        style={{ objectFit: "cover" }}
                        onError={(e) => (e.currentTarget.src = "/img/placeholder.png")}
                      />
                    ) : (
                      <div className="w-100 h-100" />
                    )}
                  </div>
                </Link>

                <div className="card-body py-2 d-flex flex-column">
                  <div className="fw-semibold" style={{ fontSize: ".92rem" }}>
                    <span
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {p.title || p.name || "Produit"}
                    </span>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="fw-bold">{fmtXof(price)}</div>
                    {!inStock && <span className="badge text-bg-secondary">Rupture</span>}
                  </div>

                  <div className="mt-auto pt-2">
                    <button
                      type="button"
                      className="btn btn-dark btn-sm w-100"
                      disabled={!inStock}
                      onClick={() => onAdd?.(p, 1)}
                    >
                      <i className="bi bi-bag-plus me-2" />
                      Afficher tailles
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* =========================
   Vue ROW : Liste
========================= */
function ProductsRowList({ items = [], onAdd, title }) {
  return (
    <section className="mb-3">
      {title ? <h2 className="h6 mb-2">{title}</h2> : null}

      <div className="vstack gap-2">
        {items.map((p) => {
          const img = p?.images?.[0] || "";
          const inStock = isInStockProduct(p);
          const price = Number(p?.price ?? 0);

          return (
            <div key={p.id || p.slug} className="card shadow-sm border-0">
              <div className="card-body py-2 py-md-3">
                <div className="d-flex gap-3 align-items-center">
                  <Link to={getProductHref(p)} className="ggf-rowitem__imgwrap">
                    {img ? (
                      <img
                        src={img}
                        alt={p.title || p.name || "Produit"}
                        loading="lazy"
                        onError={(e) => (e.currentTarget.src = "/img/placeholder.png")}
                      />
                    ) : (
                      <div className="bg-light w-100 h-100" />
                    )}
                  </Link>

                  <div className="flex-grow-1 min-w-0">
                    <Link
                      to={getProductHref(p)}
                      className="text-decoration-none text-dark fw-bold ggf-rowitem__title"
                      title={p.title || p.name}
                    >
                      {p.title || p.name || "Produit"}
                    </Link>

                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                      {inStock ? (
                        <span className="badge text-bg-success">En stock</span>
                      ) : (
                        <span className="badge text-bg-secondary">Rupture</span>
                      )}
                    </div>
                  </div>

                  <div className="ggf-rowitem__actions">
                    <div className="fw-bold text-end" style={{ whiteSpace: "nowrap" }}>
                      {fmtXof(price)}
                    </div>

                    <div className="d-grid gap-2 mt-2">
                      <button
                        type="button"
                        className="btn btn-dark btn-sm"
                        disabled={!inStock}
                        onClick={() => onAdd?.(p, 1)}
                      >
                        <i className="bi bi-bag-plus d-inline d-sm-none" />
                        <span className="d-none d-sm-inline">
                          <i className="bi bi-bag-plus me-2" />
                          Afficher tailles
                        </span>
                      </button>

                      <Link to={getProductHref(p)} className="btn btn-outline-dark btn-sm">
                        Voir
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <style>{`
                .ggf-rowitem__imgwrap{
                  width: 84px; height: 84px;
                  border-radius: 14px;
                  overflow: hidden;
                  background: #f3f4f6;
                  border: 1px solid rgba(0,0,0,.06);
                  flex: 0 0 auto;
                  display:block;
                }
                .ggf-rowitem__imgwrap img{ width:100%;height:100%;object-fit:cover;display:block; }
                .ggf-rowitem__title{
                  font-size: .98rem;
                  line-height: 1.15;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  -webkit-box-orient: vertical;
                  overflow: hidden;
                }
                .ggf-rowitem__actions{
                  width: 172px;
                  flex: 0 0 172px;
                  display:flex;
                  flex-direction: column;
                  align-items: stretch;
                }
                @media (max-width: 575.98px){
                  .ggf-rowitem__imgwrap{ width: 72px; height: 72px; border-radius: 12px; }
                  .ggf-rowitem__actions{ width: 128px; flex-basis: 128px; }
                }
              `}</style>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function HomePublic() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [sections, setSections] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [error, setError] = useState("");

  const [promo, setPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(true);
  const [, setPromoTop] = useState(null);
  const [, setPromoMid] = useState(null);
  const [, setPromoBottom] = useState(null);

  const { addItem } = useCart();
  const navigate = useNavigate();

  const [selectedCat, setSelectedCat] = useState(null);
  const selectedCatId = selectedCat ? selectedCat.id || selectedCat.slug : null;
  const isAllCategoriesView = !selectedCatId;

  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("home_viewMode_v1") || "card";
    } catch {
      return "card";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("home_viewMode_v1", viewMode);
    } catch {}
  }, [viewMode]);

  const onSelectCategory = useCallback((cat) => {
    setSelectedCat(cat || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  /* ---------- Ajout panier “variant-aware” ---------- */
  const handleAdd = useCallback(
    (p, qty = 1, meta = {}) => {
      if (!p) return;
      if (!isInStockProduct(p)) return;

      const productId = p.id || p.slug;
      const price = Number(p.price ?? 0);
      if (!productId || !Number.isFinite(price)) return;

      if (meta?.variantKey) {
        if (meta?.variant && Number(meta.variant?.stock || 0) <= 0) return;

        addItem({
          productId,
          variantKey: meta.variantKey,
          variant: meta.variant || null,
          variantLabel: meta.variantLabel || undefined,
          title: p.title || p.name || "Produit",
          price,
          image: p.images?.[0] || "",
          qty: Math.max(1, Number(qty) || 1),
          maxQty: Number(meta?.variant?.stock ?? p?.stock ?? 0),
        });
        return;
      }

      const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
      if (hasVariants) {
        const slug = p.slug || p.id;
        if (slug) navigate(`/p/${slug}`);
        return;
      }

      if (Number(p.stock || 0) <= 0) return;

      addItem({
        productId,
        variantKey: "default",
        variant: null,
        title: p.title || p.name || "Produit",
        price,
        image: p.images?.[0] || "",
        qty: Math.max(1, Number(qty) || 1),
        maxQty: Number(p?.stock ?? 0),
      });
    },
    [addItem, navigate]
  );

  const loadAll = useCallback(async () => {
    setError("");

    setLoading(true);
    try {
      const res = await listActiveProducts({ pageSize: 120 });
      const raw = Array.isArray(res?.items) ? res.items : [];
      const filtered = dedupeByIdOrSlug(raw.filter(isInStockProduct));
      setItems(filtered);
    } catch (e) {
      console.error(e);
      setItems([]);
      setError("Impossible de charger les nouveautés. Vérifie ta connexion puis réessaie.");
    } finally {
      setLoading(false);
    }

    setLoadingMeta(true);
    try {
      const c = await listPublicCategories({});
      const catsAll = Array.isArray(c) ? c : [];
      setCats(catsAll.filter((x) => x?.visible === true));
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMeta(false);
    }

    try {
      const s = await listActiveHomeSections({});
      setSections(Array.isArray(s) ? s : []);
    } catch (e) {
      console.error(e);
      setSections([]);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  /* ---------- Promo globale ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPromoLoading(true);
        const sp = new URLSearchParams(window.location.search);
        const previewId = sp.get("promoId") || undefined;

        if (!previewId) {
          const cached = sessionStorage.getItem("active_promo_v1");
          if (cached) {
            try {
              if (!cancelled) setPromo(JSON.parse(cached));
            } catch {}
          }
        }

        const p = await getActivePromo(previewId);
        if (cancelled) return;
        setPromo(p || null);

        if (!previewId) {
          if (p) sessionStorage.setItem("active_promo_v1", JSON.stringify(p));
          else sessionStorage.removeItem("active_promo_v1");
        }
      } catch {
        if (!cancelled) setPromo(null);
      } finally {
        if (!cancelled) setPromoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------- Promos par placement ---------- */
  useEffect(() => {
    (async () => setPromoTop(await getActivePromoForPlacement("home_top")))();
    (async () => setPromoMid(await getActivePromoForPlacement("home_mid")))();
    (async () => setPromoBottom(await getActivePromoForPlacement("home_bottom")))();
  }, []);

  /* ---------- Filtrage par catégorie ---------- */
  const filteredItems = useMemo(() => {
    if (!selectedCatId) return items;
    return items.filter((p) => String(p.categoryId || "") === String(selectedCatId));
  }, [items, selectedCatId]);

  const safePromo = useMemo(() => {
    if (!promo) return null;
    if (!Array.isArray(promo?.items)) return promo;
    return { ...promo, items: promo.items.filter(isInStockProduct) };
  }, [promo]);

  /* ---------- ✅ Featured (ALÉATOIRE) ---------- */
  const FEATURED_TAKE = 4;
  const shouldShowFeaturedZone =
    isAllCategoriesView && !loading && filteredItems.length >= FEATURED_TAKE;

  const featuredRandom = useMemo(() => {
    if (!shouldShowFeaturedZone) return [];
    return shuffle(filteredItems).slice(0, FEATURED_TAKE);
  }, [filteredItems, shouldShowFeaturedZone]);

  const remaining = useMemo(() => {
    if (!shouldShowFeaturedZone) return filteredItems;
    const used = new Set(featuredRandom.map((p) => p?.id || p?.slug));
    return filteredItems.filter((p) => !used.has(p?.id || p?.slug));
  }, [filteredItems, shouldShowFeaturedZone, featuredRandom]);

  const ViewToggle = (
    <div className="d-inline-flex gap-2" role="group" aria-label="Changer la vue">
  <button
    type="button"
    className={`btn btn-sm ${viewMode === "row" ? "btn-dark" : "btn-outline-dark"}`}
    onClick={() => setViewMode("row")}
  >
    <i className="bi bi-list-ul me-1" />
    <span className="d-none d-sm-inline"></span>
  </button>

  <button
    type="button"
    className={`btn btn-sm ${viewMode === "card" ? "btn-dark" : "btn-outline-dark"}`}
    onClick={() => setViewMode("card")}
  >
    <i className="bi bi-grid-3x3-gap me-1" />
    <span className="d-none d-sm-inline"></span>
  </button>
</div>

  );

  return (
    <div className="container py-3 py-md-4">
      <Seo
        title={`GOD'S GIFT FASHION | Prêt-à-porter, nouveautés & promos`}
        description={`Boutique de prêt-à-porter : nouveautés, promos, paiement Mobile Money et livraison 24–48h.`}
        canonical={`${window.location.origin}/`}
      />

      {!loadingMeta && (
        <MobileCategoryBar cats={cats} selectedId={selectedCatId} onSelect={onSelectCategory} />
      )}

      <div className="row g-3 g-lg-4">
        <div className="col-lg-3">
          {!loadingMeta && (
            <CategorySidebar cats={cats} selectedId={selectedCatId} onSelect={onSelectCategory} />
          )}
        </div>

        <div className="col-lg-9">
          <section className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h1 className="h5 m-0">{selectedCat ? selectedCat.name : "Nouveautés"}</h1>
              <div className="small text-muted">
                {selectedCat ? "Produits de la catégorie sélectionnée" : "Découvrez les derniers arrivages"}
              </div>
            </div>

            <div className="d-flex gap-2 align-items-center flex-wrap">
              {ViewToggle}

              {selectedCat && (
                <button className="btn btn-outline-dark btn-sm" onClick={() => onSelectCategory(null)}>
                  Tout afficher
                </button>
              )}

              <button className="btn btn-outline-dark btn-sm" onClick={loadAll}>
                Rafraîchir
              </button>
            </div>
          </section>

          {error && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center">
              <div>{error}</div>
              <button className="btn btn-sm btn-dark" onClick={loadAll}>
                Réessayer
              </button>
            </div>
          )}

          {/* Promo (seulement quand "Tout") */}
          {!promoLoading &&
            safePromo &&
            Array.isArray(safePromo?.items) &&
            safePromo.items.length > 0 &&
            isAllCategoriesView && <PromoProducts promo={safePromo} onAdd={handleAdd} />}

          {loading && (viewMode === "card" ? <SkeletonGrid n={8} /> : <SkeletonRows n={8} />)}

          {!loading && filteredItems.length > 0 && (
            <>
              {/* ✅ Featured au style de ton image (uniquement quand "Tout") */}
              {shouldShowFeaturedZone && isAllCategoriesView && (
                <>
                  <FeaturedShowcase items={featuredRandom} />
                  <div className="mb-3">
                    <BannersSliderFirestore placement="after_featured" take={10} loopVideos={false} />
                  </div>
                </>
              )}

              {/* ✅ si catégorie choisie => pas de zone mis en avant */}
              {!isAllCategoriesView ? (
                viewMode === "card" ? (
                  <ProductsCardGrid
                    title={`Produits · ${selectedCat?.name || ""}`}
                    items={filteredItems}
                    onAdd={handleAdd}
                  />
                ) : (
                  <ProductsRowList
                    title={`Produits · ${selectedCat?.name || ""}`}
                    items={filteredItems}
                    onAdd={handleAdd}
                  />
                )
              ) : (
                <>
                  {remaining.length > 0 ? (
                    viewMode === "card" ? (
                      <ProductsCardGrid title="À découvrir" items={remaining} onAdd={handleAdd} />
                    ) : (
                      <ProductsRowList title="À découvrir" items={remaining} onAdd={handleAdd} />
                    )
                  ) : (
                    <div className="text-muted">Aucun produit en stock pour le moment.</div>
                  )}
                </>
              )}
            </>
          )}

          {!loading && filteredItems.length === 0 && (
            <div className="alert alert-light">
              Aucun produit trouvé pour cette catégorie.
              <div className="mt-2">
                <button className="btn btn-sm btn-outline-dark" onClick={() => onSelectCategory(null)}>
                  Tout afficher
                </button>
              </div>
            </div>
          )}

          <HomeRenderer sections={sections} onAdd={handleAdd} />
        </div>
      </div>
    </div>
  );
}






